/**
 * @octomil/browser — In-browser ML inference via ONNX Runtime Web + WebGPU
 *
 * @example
 * ```ts
 * import { OctomilClient } from '@octomil/browser';
 *
 * const ml = new OctomilClient({
 *   model: 'https://models.octomil.com/sentiment-v1.onnx',
 *   backend: 'webgpu',
 * });
 *
 * await ml.load();
 * const result = await ml.predict({ text: 'This is amazing!' });
 * console.log(result.label, result.score);
 * ml.close();
 * ```
 *
 * @packageDocumentation
 */

// Main class
export { OctomilClient } from "./octomil.js";

// Unified facade (cloud-backed responses, no model required at construction)
export { Octomil, OctomilNotInitializedError, FacadeEmbeddings } from "./facade.js";
export type { OctomilFacadeOptions } from "./facade.js";

// Local lifecycle status types
export { buildBrowserLifecycleStatus, buildBrowserUnavailableStatus } from "./local-lifecycle.js";
export type {
  BrowserLocalLifecycleStatus,
  BrowserCacheStatus,
  BrowserLocalProvider,
} from "./local-lifecycle.js";

// Audio namespace
export { OctomilAudio } from "./audio/octomil-audio.js";
export { AudioTranscriptions } from "./audio/audio-transcriptions.js";
export type { TranscriptionRequest } from "./audio/audio-transcriptions.js";
export type {
  TranscriptionResult,
  TranscriptionSegment,
} from "./audio/transcription-types.js";
export { AudioVad } from "./audio/audio-vad.js";
export type {
  VadDetectRequest,
  VadDetectResponse,
  VadTransition,
} from "./audio/audio-vad.js";
export { SpeakerEmbedding } from "./audio/speaker-embedding.js";
export type {
  SpeakerEmbeddingCreateRequest,
  SpeakerEmbeddingCreateResponse,
} from "./audio/speaker-embedding.js";
export { AudioDiarization } from "./audio/audio-diarization.js";
export type {
  DiarizationCreateRequest,
  DiarizationCreateResponse,
  DiarizationSegment,
} from "./audio/audio-diarization.js";
export { AudioTtsStream } from "./audio/audio-tts-stream.js";
export type {
  TtsStreamRequest,
  TtsStreamResponse,
  TtsStreamMetadata,
} from "./audio/audio-tts-stream.js";

// Chat namespace (OpenAI-compatible chat completions)
export { ChatClient, messagesToResponseInput } from "./chat.js";
export type {
  ChatClientOptions,
  ChatThread,
  ChatTurnRequest,
} from "./chat.js";

// Sub-modules (for advanced usage)
export { InferenceEngine } from "./runtime/engines/onnx-web/engine.js";
export type { ModelRuntime } from "./runtime/index.js";
export { ModelManager } from "./model-manager.js";
export { createModelCache, type ModelCache } from "./cache.js";
export {
  TelemetryReporter,
  initTelemetry,
  getTelemetry,
  closeTelemetry,
} from "./telemetry.js";
export type {
  TelemetryReporterOptions,
  TelemetryResource,
  TelemetryEnvelope,
  OtlpKeyValue,
  OtlpResource,
  OtlpInstrumentationScope,
  OtlpLogRecord,
  OtlpScopeLogs,
  OtlpResourceLogs,
  ExportLogsServiceRequest,
} from "./telemetry.js";
export { MonitoringClient } from "./monitoring.js";
export type { AlertRule, UpdateAlertRuleRequest } from "./monitoring.js";
export { SettingsClient } from "./settings.js";
export type {
  BillingSession,
  BillingState,
  UsageLimits,
  Integration,
  IntegrationValidation,
  IntegrationPatch,
} from "./settings.js";
export { TrainingClient } from "./training.js";
export type { TrainingJob, TrainingJobStatus } from "./training.js";

// Capabilities
export { CapabilitiesClient } from "./capabilities.js";
export type { CapabilityProfile } from "./capabilities.js";

// Control (device registration + heartbeat)
export { ControlClient } from "./control.js";
export type {
  DeviceRegistration,
  HeartbeatResponse,
  ControlClientOptions,
  DesiredState,
  DesiredModelEntry,
  DeviceSyncRequest,
  DeviceSyncResponse,
  ObservedModelStatus,
} from "./control.js";
export type { ControlSyncResult } from "./types.js";
export { DevicesClient } from "./devices.js";

// Sync (desired-state reconciliation)
export { SyncManager } from "./sync-manager.js";
export type {
  SyncManagerOptions,
  SyncEvent,
  SyncEventListener,
  LocalModelMeta,
} from "./sync-manager.js";

// Device auth
export { DeviceAuth } from "./device-auth.js";
export { ArtifactsClient } from "./artifacts.js";
export type {
  ArtifactManifest,
  ArtifactDownloadUrls,
  ArtifactDownloadUrlsRequest,
} from "./artifacts.js";

// Silent device registration
export { configure, getDeviceContext } from "./configure.js";
export type { ConfigureOptions } from "./configure.js";
export { DeviceContext } from "./device-context.js";
export type { RegistrationState, TokenState } from "./device-context.js";
export type {
  SilentAuthConfig,
  PublishableKeyEnvironment,
} from "./silent-auth-config.js";
export {
  validatePublishableKey,
  getPublishableKeyEnvironment,
} from "./silent-auth-config.js";
export type { MonitoringConfig } from "./monitoring-config.js";

// Model integrity
export {
  computeHash,
  verifyModelIntegrity,
  assertModelIntegrity,
} from "./integrity.js";

// Responses namespace (Layer 2 — structured response API)
export {
  ResponsesClient,
  generateId as generateResponseId,
} from "./responses.js";
export { ToolRunner } from "./responses-tools.js";
export { createTransformersJsLocalResponsesRuntime } from "./transformers-local-runtime.js";
export type {
  ToolExecutor,
  ToolResult,
} from "./responses-tools.js";
export type {
  LocalResponsesRuntime,
  LocalResponsesRuntimeResolver,
} from "./responses-runtime.js";
export type { TransformersLocalResponsesRuntimeOptions } from "./transformers-local-runtime.js";
export type {
  ResponseRequest,
  ContentBlock,
  ResponseInputItem,
  ChatContentPart,
  ToolDef,
  ResponseToolCall,
  ResponseOutput,
  Response as ResponseObject,
  ResponseUsage,
  TextDeltaEvent,
  ToolCallDeltaEvent,
  DoneEvent,
  ResponseStreamEvent,
  ResponsesClientOptions,
} from "./responses.js";

// Models namespace (status / load / unload / list / clearCache)
export { ModelsClient } from "./models.js";
export type { ModelStatus, CachedModelInfo } from "./models.js";

// Streaming inference
export { StreamingInferenceEngine } from "./streaming.js";

// Engine registry & plugin interface
export { EngineRegistry } from "./runtime/index.js";
export type { EnginePlugin } from "./runtime/index.js";

// Attempt runner (browser-safe local lifecycle)
export { BrowserAttemptRunner } from "./runtime/index.js";
export type {
  Locality,
  Mode,
  AttemptStage,
  AttemptStatus,
  GateStatus,
  GateCode,
  GateResult,
  RouteAttempt,
  AttemptArtifact,
  ArtifactCacheStatus,
  FallbackTrigger,
  AttemptLoopResult,
  CandidateGate,
  CandidatePlan,
  EndpointChecker,
  RuntimeChecker,
  ArtifactChecker,
} from "./runtime/index.js";

// Output quality evaluators (browser-safe, built-in gate evaluators)
export {
  JsonParseableEvaluator,
  JsonSchemaEvaluator,
  ToolCallValidEvaluator,
  RegexPredicateEvaluator,
  SafetyPassedEvaluator,
  EvaluatorRegistry,
  RegistryBackedEvaluator,
  extractText,
  extractToolCalls,
} from "./runtime/index.js";
export type {
  EvaluatorResult,
  GateEvaluator,
  SafetyCheckFn,
} from "./runtime/index.js";

// Browser runtime resolver (WebGPU/WASM probing + artifact cache checks)
export {
  BrowserRuntimeChecker,
  BrowserArtifactChecker,
} from "./runtime/index.js";

// Production routing (request router + model refs + route events)
export {
  BrowserRequestRouter,
  FetchEndpointChecker,
  parseModelRef,
} from "./runtime/index.js";
export type {
  BrowserRoutingContext,
  BrowserRoutingDecision,
  PlannerResult,
  RouteMetadata,
  ModelRef,
  ModelRefKind,
} from "./runtime/index.js";

// Route events (canonical cross-SDK telemetry shape)
export {
  stripForbiddenKeys,
  findForbiddenKeys,
  buildAttemptDetail,
  generateCorrelationId,
  FORBIDDEN_TELEMETRY_KEYS,
} from "./route-event.js";
export type {
  BrowserRouteEvent,
  RouteAttemptDetail,
  GateSummary,
} from "./route-event.js";

// Embeddings
export { embed } from "./embeddings.js";
export { OctomilText, TextPredictionsClient } from "./text/octomil-text.js";
export type {
  TextPredictionRequest,
  TextPredictionResult,
} from "./text/octomil-text.js";

// Federated training
export { FederatedClient, WeightExtractor } from "./federated.js";

// Federated analytics
export { FederatedAnalyticsClient } from "./federated-analytics.js";

// Secure aggregation
export {
  SecureAggregation,
  SecAggPlus,
  shamirSplit,
  shamirReconstruct,
} from "./secure-aggregation.js";

// Privacy filters
export {
  clipGradients,
  addGaussianNoise,
  quantize,
  dequantize,
} from "./privacy.js";

// Install ID (telemetry resource attribute)
export { getInstallId, resetInstallIdCache } from "./install-id.js";

// Gradient cache
export { GradientCache } from "./gradient-cache.js";

// Experiments / A/B testing
export { ExperimentsClient } from "./experiments.js";

// Routing
export { RoutingClient, detectDeviceCapabilities } from "./routing.js";

// Planner (runtime planner types + policy validation)
export {
  RoutingPolicy,
  VALID_ROUTING_POLICIES,
  LOCAL_ONLY_POLICIES,
  validateRoutingPolicy,
  assertBrowserCompatiblePolicy,
} from "./planner/index.js";
export type {
  RoutingPolicyName,
  RouteExecution,
  RouteModelRequested,
  RouteModelResolved,
  RouteModel,
  ArtifactCache,
  RouteArtifact,
  PlannerInfo,
  FallbackInfo,
  RouteReason,
  RouteMetadata as PlannerRouteMetadata,
  RuntimeArtifactPlan,
  RuntimeCandidatePlan,
  RuntimePlanResponse,
  RuntimeSelection,
} from "./planner/index.js";

// Types
export type {
  AuthConfig,
  OrgApiKeyAuth,
  DeviceTokenAuth,
  OctomilOptions,
  Backend,
  CacheStrategy,
  DownloadProgress,
  TensorData,
  NamedTensors,
  PredictInput,
  PredictOutput,
  ChatRole,
  ChatMessage,
  ChatResponse,
  ChatOptions,
  ChatChunk,
  CacheInfo,
  TelemetryEvent,
  ModelMetadata,
  OctomilErrorCode,
  DeviceAuthConfig,
  DeviceAuthToken,
  DeviceInfo,
  StreamingModality,
  StreamingOptions,
  StreamingChunk,
  StreamingResult,
  StreamToken,
  WeightMap,
  TrainingConfig,
  TrainStepResult,
  FederatedRound,
  Experiment,
  ExperimentVariant,
  DeviceCapabilities,
  RoutingPreference,
  RoutingRequest,
  RoutingDecision,
  RoutingFallbackTarget,
  RoutingConfig,
  CloudInferenceRequest,
  CloudInferenceResponse,
  EmbeddingResult,
  EmbeddingUsage,
  EmbeddingResponse,
  BenchmarkResult,
  DetectionResult,
  RankedEngine,
  InferenceMetrics,
  GenerationChunk,
  CacheStats,
  AnalyticsFilter,
  DescriptiveResult,
  GroupStats,
  ConfidenceInterval,
  TTestResult,
  ChiSquareResult,
  PostHocPair,
  AnovaResult,
  AnalyticsQuery,
  AnalyticsQueryListResponse,
  GradientCacheEntry,
} from "./types.js";

export type { QuantizedWeightMap } from "./privacy.js";

export {
  OctomilError,
  ERROR_CODE_MAP,
  AuthType,
  PrincipalType,
  Scope,
} from "./types.js";

// Contract-generated enums and constants (from octomil-contracts)
export {
  ErrorCode,
  ModelStatus as ContractModelStatus,
  DeviceClass,
  FinishReason,
  CompatibilityLevel,
  OTLP_RESOURCE_ATTRIBUTES,
} from "./_generated/index.js";

// Helpers
import type { BenchmarkResult } from "./types.js";
export function benchmarkResultOk(result: BenchmarkResult): boolean {
  return result.error == null;
}
